import { ICON_COMPONENTS } from "./iconChoices";

// Rayon par bouton ("{prefix}_radius", voir backend fields.py
// register_button_style_fields) — recouvre PARTIELLEMENT le registre
// global de forme de la boutique (applyShape.js / tailwind.config.js
// rounded-btn) : "theme" délègue à ce réglage global (même classe
// rounded-btn, donc suit --radius-btn), les autres valeurs forcent une
// forme propre à CE bouton, y compris "pill" qui n'existe pas côté global.
const RADIUS_CLASSNAMES = {
  theme: "rounded-btn",
  sharp: "rounded-none",
  soft: "rounded-xl",
  rounded: "rounded-2xl",
  pill: "rounded-full",
};

function isLightColor(hex) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  // Luminance perçue simplifiée (pondération ITU-R BT.601) — seuil 150,
  // suffisant pour choisir un texte noir/blanc lisible sans viser une
  // conformité WCAG stricte : ce réglage reste esthétique (couleur libre
  // choisie par l'admin), pas une garantie d'accessibilité universelle.
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

/**
 * Résout le style d'UN bouton (couleur, transparence, forme, icône +
 * texte/icône seule) à partir de son groupe de props
 * "{prefix}_color/_opacity/_radius/_icon_mode/_icon" — un seul endroit pour
 * cette logique, partagé par toutes les sections/composants qui rendent un
 * bouton (voir HeroSection.jsx, ImageBannerSection.jsx, Header.jsx, etc.),
 * plutôt que de la dupliquer fichier par fichier.
 *
 * `autoIcon` (nom d'icône du registre ICON_CHOICES, ou null) : à fournir
 * quand l'appelant détecte lui-même un cas historique où CE bouton précis
 * affichait déjà une icône avant l'introduction de ce champ (ex:
 * HeroSection.jsx passe "Phone" quand cta_secondary_url commence par
 * "tel:"). `fallbackIcon` : icône affichée par défaut en mode "auto" quand
 * aucun autre cas ne s'applique (ex: la flèche ArrowRight déjà affichée par
 * les liens texte du Hero/promo_banner) — laisser vide pour un bouton qui
 * n'affichait ORIGINELLEMENT aucune icône (la quasi-totalité des boutons du
 * site). Sans ces deux options, "auto" (valeur par défaut de icon_mode) ne
 * montre jamais d'icône, comportement d'origine inchangé.
 */
export function resolveButtonStyle(props, prefix, { autoIcon = null, fallbackIcon = null } = {}) {
  const color = props?.[`${prefix}_color`] || "";
  const opacityRaw = props?.[`${prefix}_opacity`];
  const opacity = opacityRaw === undefined || opacityRaw === "" ? 100 : Number(opacityRaw);
  const radius = props?.[`${prefix}_radius`] || "theme";
  const iconMode = props?.[`${prefix}_icon_mode`] || "auto";
  const chosenIcon = props?.[`${prefix}_icon`] || "";

  const style = {};
  if (Number.isFinite(opacity) && opacity < 100) {
    style.opacity = Math.max(0, opacity) / 100;
  }
  if (color) {
    style.backgroundColor = color;
    style.borderColor = color;
    style.color = isLightColor(color) ? "#0A0E17" : "#FFFFFF";
  }

  let iconName = null;
  let showIcon = false;
  let showText = true;
  if (iconMode === "text") {
    // Override explicite : jamais d'icône, même si l'admin en a choisi une
    // (permet de masquer une flèche/icône historique sans devoir la retirer).
  } else if (iconMode === "icon_only" || iconMode === "icon_text") {
    iconName = chosenIcon || autoIcon || fallbackIcon || null;
    showIcon = !!iconName;
    showText = iconMode === "icon_text" || !iconName;
  } else {
    // "auto" — comportement d'origine du bouton, éventuellement enrichi par
    // une icône explicitement choisie sans changer de mode.
    iconName = chosenIcon || autoIcon || fallbackIcon || null;
    showIcon = !!iconName;
  }

  return {
    className: RADIUS_CLASSNAMES[radius] || RADIUS_CLASSNAMES.theme,
    style,
    Icon: iconName ? ICON_COMPONENTS[iconName] || null : null,
    showIcon,
    showText,
  };
}
